<template>
    <v-card class="content-container">
        <div class="form-header">
            <span>{{ title }}</span>
            <span
                aria-hidden="true"
                class="form-dismiss"
                @click="remove()"
                v-if="removable && !isReviewAction">
                X
            </span>
        </div>

        <v-divider class="form-divider"></v-divider>

        <v-form
            class="form-box"
            ref="form"
            lazy-validation>

            <!-- START DATE -->
            <div class="form-input">
                <span class="form-label">
                    {{ formLabel.startDate }}
                </span>
                <v-menu
                    v-model="dpStartDateShow"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                    >
                    <template v-slot:activator="{ on }">
                        <v-text-field
                            v-model="formattedStartDate"
                            prepend-inner-icon="event"
                            @click:prepend="dpStartDateShow = true"
                            :error-messages="errors.startDate"
                            :disabled="isReviewAction"
                            solo dense
                            height="40px"
                            readonly
                            v-on="on"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                        :locale="localeLang"
                        v-model="form.startDate"
                        @input="dpStartDateShow = false">
                    </v-date-picker>
                </v-menu>
            </div>

            <!-- END DATE -->
            <div class="form-input">
                <span class="form-label">
                    {{ formLabel.endDate }}
                </span>
                <v-menu
                    v-model="dpEndDateShow"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                    >
                    <template v-slot:activator="{ on }">
                        <v-text-field
                            v-model="formattedEndDate"
                            prepend-inner-icon="event"
                            @click:prepend="dpEndDateShow = true"
                            :error-messages="errors.endDate"
                            :disabled="isReviewAction"
                            solo dense
                            height="40px"
                            readonly
                            v-on="on"
                        ></v-text-field>
                    </template>
                    <v-date-picker
                        :locale="localeLang"
                        v-model="form.endDate"
                        @input="dpEndDateShow = false">
                    </v-date-picker>
                </v-menu>
            </div>

            <v-row>
                <!-- START TIME -->
                <v-col cols="6" class="pt-0 pb-0">
                    <div class="form-input">
                        <span class="form-label">
                            {{ formLabel.startTime }}
                        </span>
                        <v-text-field
                            v-model="form.startTime"
                            autocomplete="off"
                            type="time"
                            step="60"
                            solo dense
                            height="40px"
                            :rules="inputRule"
                            :error-messages="errors.startTime"
                            :disabled="isReviewAction"
                            placeholder=" ">
                        </v-text-field>
                    </div>
                </v-col>

                <!-- OT TIME -->
                <v-col cols="6" class="pt-0 pb-0">
                    <div>
                        <span class="form-label">
                            {{ formLabel.otTime }}
                        </span>
                        <hours-input
                            v-model="form.otTime"
                            :error-messages="errors.otTime"
                            :disabled="isReviewAction"
                            outlined dense
                        />
                    </div>
                </v-col>
            </v-row>

            <!-- REASON -->
            <div class="form-input">
                <span class="form-label">
                    {{ formLabel.reason }}
                </span>
                <v-textarea
                    v-model="form.description"
                    rows="3"
                    autocomplete="off"
                    :placeholder="formLabel.reasonPlaceholder"
                    no-resize solo dense
                    :rules="inputRule"
                    :disabled="isReviewAction"
                    :error-messages="errors.description">
                </v-textarea>
            </div>
        </v-form>
    </v-card>
</template>

<script>
import HoursInput from '@/components/HoursInput'

export default {
    name: 'OvertimeRequestForm',

    components: {
        HoursInput
    },

    props: {
        // form title
        title: {
            type: String,
            default: 'Inquiry'
        },

        // form initial value
        value: {
            type: Object,
            default: () => ({})
        },

        // is component removable
        removable: {
            type: Boolean,
            default: true
        },

        // error from parent
        errorMessages: {
            type: Object,
            default: () => ({})
        },

        isReviewAction: {
            type: Boolean,
            default: false
        }
    },

    data () {
        return {
            cacheDescription: null,
            form: Object.assign({
                startDate: this.$util.getCurrentDate(),
                endDate: this.$util.getCurrentDate(),
                dateCount: 1,
                startTime: null,
                otTime: '01:00',
                description: ''
            }, this.value),

            dpStartDateShow: false,
            dpEndDateShow: false,
            formattedStartDate: this.$util.formatDate(this.$util.getCurrentDate()),
            formattedEndDate: this.$util.formatDate(this.$util.getCurrentDate()),

            formErrors: {
                startDate: null,
                endDate: null,
                startTime: null,
                otTime: null,
                reason: null
            }
        }
    },

    computed: {
        localeLang () {
            return this.$vuetify.lang.current
        },

        formLabel () {
            return {
                startDate: this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.startDate'),
                endDate: this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.endDate'),
                startTime: this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.startTime'),
                otTime: this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.otTime'),
                reason: this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.reason'),
                reasonPlaceholder: this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.reasonPlaceholder')
            }
        },

        formErrorMessages () {
            return {
                startDateOverlap: this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.startDateOverlap'),
                endDateOverlap: this.$vuetify.lang.t('$vuetify.liffOvertimeRequest.endDateOverlap')
            }
        },

        /**
         * combine form internal error with parent assigned error
         * - [internal error] error occur in this form
         * - [parent error] error from validation of all form in this request
         */
        errors () {
            const errors = {}
            const keys = Object.keys(this.formErrors)

            for (let i = 0; i < keys.length; i++) {
                errors[keys[i]] = this.formErrors[keys[i]] || this.errorMessages[keys[i]]
            }

            return errors
        },

        /**
         * determine whether this form is valid or not through error message.
         * if all keys in errors object has false value (which should be false),
         * this form is valid.
         */
        valid () {
            const vals = Object.values(this.errors)
            return vals.every(v => !v)
        },

        inputRule () {
            return [
                v => !!v || 'This field is required'
            ]
        }
    },

    watch: {
        form: {
            deep: true,
            handler (v) {
                if (v.description !== this.cacheDescription) {
                    this.cacheDescription = v.description

                    const self = this
                    this.$util.resetTimeout('bounceDescription', () => {
                        self.validateSchedule(v)
                    }, 300)
                } else {
                    this.validateSchedule(v)
                }
            }
        }
    },

    methods: {
        canSubmit () {
            // this.$refs.form.validate() -> validate if input value existed
            // this.valid -> validate form value
            if (!this.valid || !this.$refs.form.validate()) {
                return false
            }

            return true
        },

        /**
         * internally validate form value
         */
        validateSchedule (v) {
            this.formattedStartDate = this.$util.formatDate(v.startDate)
            this.formattedEndDate = this.$util.formatDate(v.endDate)

            if (v.startDate > v.endDate) {
                this.formErrors.startDate = this.formErrorMessages.startDateOverlap
                this.formErrors.endDate = this.formErrorMessages.endDateOverlap
                return
            } else {
                this.dateCount = this.getDateCount(v.startDate, v.endDate)
                this.formErrors.startDate = null
                this.formErrors.endDate = null
            }

            const [otTimeHour, otTimeMinute] = v.otTime.split(':')
            if (v.startTime && otTimeHour && otTimeMinute) {
                this.$emit('data', { ...v, dateCount: this.dateCount })
            }
        },

        getDateCount (startDateStr, endDateStr) {
            const diffMS = ((new Date(endDateStr)) - (new Date(startDateStr)))
            return (diffMS / (24 * 60 * 60 * 1000)) + 1 // get date count in days
        },

        remove () {
            this.$emit('remove')
        },

        /**
         * get ot time in minutes
         */
        getOTTime () {
            if (this.form.startTime) {
                const totalDays = new Date(this.form.endDate).getDate() - new Date(this.form.startDate).getDate() + 1
                const [otTimeHour, otTimeMinute] = this.form.otTime.split(':')
                const hours = parseInt(otTimeHour, 10)
                const minutes = parseInt(otTimeMinute, 10)

                if (hours > 0 || minutes > 0) {
                    return ((hours * 60) + minutes) * totalDays
                }
            }

            return 0
        },

        formattedDate () {
            if (Object.keys(this.value).length !== 0) {
                if (this.value.startDate) {
                    this.formattedStartDate = this.$util.formatDate(this.value.startDate)
                }
                if (this.value.endDate) {
                    this.formattedEndDate = this.$util.formatDate(this.value.endDate)
                }
            }
        }
    },

    mounted () {
        this.cacheDescription = this.form.description
        this.formattedDate()
    }
}
</script>

<style lang="scss" scoped>
.content-container {
    box-shadow: 0 0 25px 0 rgba(0, 0, 0, 0.08) !important;

    .form-header {
        padding: 1rem 1.5rem 1rem 1.5rem;
        display: flex;
        justify-content: space-between;
        font-size: 16px;
        font-weight: bold;
        color: #131410;
    }

    .form-dismiss {
        font-size: 1rem;
        font-weight: bold;
        line-height: 1;
        color: #6C6C6C;
        display: flex;
        align-items: center;
    }

    .form-divider {
        background-color: #A8A8A855;
    }

    .form-box {
        padding: 1rem 1.5rem 0.5rem 1.5rem;
    }
}

.form-label {
    font-size: 14px;
    color: #757575;
    line-height: 2;
}

::v-deep .form-input {
    .v-input__control > .v-input__slot {
        box-shadow: none !important;
        border: 1px solid #A8A8A8 !important;
        border-radius: 5px;
    }

    & .error--text .v-input__control > .v-input__slot {
        border: 1px solid #FF5252 !important;
    }
}
</style>
